import { useState, useEffect, useRef } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { Star, LogOut, PanelLeftClose } from 'lucide-react';
import { useAppStore } from '../../stores/appStore';
import { useAuthStore } from '../../stores/authStore';
import { useChatUnread } from '../../hooks/useChatUnread';
import { useElementHeight } from '../../hooks/useElementHeight';
import { userPrefService } from '../../services/userPrefService';
import { partnerPathAllowed } from '../../utils/loginSurface';
import { NAV_STRUCTURE, SETTINGS_GROUP_ID, FAVORITE_ONLY_PAGES, filterNavItems } from './navStructure';

/**
 * İki-panel sidebar (Monday.com Vibe tarzı)
 * - Sol: dar ikon rail (her zaman görünür, 64px)
 * - Sağ: genişleyen menü paneli (grup seçince açılır, 224px)
 * - Her iki panel ayrı floating card — rounded + shadow + gap
 */

const ACTIVE_GROUP_KEY = 'vertex.sidebar.activeGroup';
const PANEL_OPEN_KEY = 'vertex.sidebar.panelOpen';

function loadActiveGroup() {
  try { return localStorage.getItem(ACTIVE_GROUP_KEY) || null } catch { return null }
}
function saveActiveGroup(id) {
  try {
    if (id) localStorage.setItem(ACTIVE_GROUP_KEY, id);
    else localStorage.removeItem(ACTIVE_GROUP_KEY);
  } catch {}
}
// Panelin açık/kapalı durumu — bir sekmeye (sayfaya) girince daralt, ikon ile tekrar aç
function loadPanelOpen() {
  try { return localStorage.getItem(PANEL_OPEN_KEY) !== '0' } catch { return true }  // varsayılan açık
}
function savePanelOpen(open) {
  try { localStorage.setItem(PANEL_OPEN_KEY, open ? '1' : '0') } catch {}
}

// ── Favoriler (En Çok Kullanılanlar) ────────────────────────────────
// Kullanıcının sık kullandığı sayfaları sabitleyip rail'in tepesindeki ⭐ panelinden hızlı erişir.
const FAV_ID = '__fav';
const FAV_KEY = 'vertex.sidebar.favorites';
// path → {label, icon, module} — favori öğesinin etiketi/ikonu NAV_STRUCTURE'dan çözülür.
const PATH_INDEX = (() => {
  const idx = {};
  for (const g of [...NAV_STRUCTURE, { items: FAVORITE_ONLY_PAGES }]) {
    for (const it of (g.items || [])) {
      if (it.path && !idx[it.path]) idx[it.path] = it;
    }
  }
  return idx;
})();
function loadFavorites() {
  try { return JSON.parse(localStorage.getItem(FAV_KEY) || '[]').filter((p) => typeof p === 'string') } catch { return [] }
}
function saveFavorites(list) {
  try { localStorage.setItem(FAV_KEY, JSON.stringify(list)) } catch {}
}
// Favoriler artık SUNUCUDA da durur (rpt_user_pref). localStorage hızlı/çevrimdışı kopya;
// tek gerçek kaynak sunucudur. Neden: 20.08.2026'da bir çökme sonrası hata sınırının
// "arayüz durumunu temizle" adımı tarayıcıdaki favorileri sildi ve geri getirilemedi —
// yalnız tarayıcıda duran veri, kullanıcının kendi girdiği içerik için yeterli değil.
const FAV_PREF_KEY = 'sidebar.favorites';

function useHasPermission() {
  return useAuthStore((s) => s.hasPermission);
}

/** Panel içi nav item (tree-indent destekli) + favori (⭐) toggle */
function PanelItem({ item, isActive, onClick, isFav, onToggleFav }) {
  const Icon = item.icon;
  return (
    <div className="relative group/pi">
      <div className="absolute top-1/2 left-0 w-2.5 h-px bg-gray-200" />
      <Link
        to={item.path}
        onClick={onClick}
        className={`flex items-center gap-2 ml-4 pl-2 pr-8 py-2 text-sm rounded-md transition-colors ${
          isActive
            ? 'bg-primary-50 text-primary-700 font-medium'
            : 'text-gray-700 hover:text-gray-900 hover:bg-gray-50'
        }`}
      >
        {Icon && <Icon size={14} className={`shrink-0 ${isActive ? 'text-primary-500' : 'text-gray-400'}`} />}
        <span className="whitespace-nowrap truncate min-w-0" title={item.label}>{item.label}</span>
      </Link>
      {onToggleFav && (
        <button
          type="button"
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); onToggleFav(item.path); }}
          title={isFav ? 'Favorilerden çıkar' : 'Favorilere ekle'}
          className={`absolute right-1.5 top-1/2 -translate-y-1/2 p-1 rounded-md transition-colors ${
            isFav ? 'text-amber-400 hover:text-amber-500'
                  : 'text-gray-300 opacity-0 group-hover/pi:opacity-100 hover:text-amber-400'
          }`}
        >
          <Star size={13} fill={isFav ? 'currentColor' : 'none'} />
        </button>
      )}
    </div>
  );
}

export default function Sidebar() {
  const { total: chatUnread } = useChatUnread();
  const { mobileSidebarOpen, setMobileSidebarOpen, activeCompany } = useAppStore();
  const location = useLocation();
  const hasPermission = useHasPermission();
  const userRole = useAuthStore((s) => s.user?.role);
  const partnerIsAdmin = useAuthStore((s) => !!s.user?.partner_is_admin);
  const userId = useAuthStore((s) => s.user?.id);
  const companyName = activeCompany === '01' ? 'Adeptek' : 'Forte';

  const [activeGroupId, setActiveGroupId] = useState(loadActiveGroup);
  const [panelOpen, setPanelOpen] = useState(loadPanelOpen);
  const [favorites, setFavorites] = useState(loadFavorites);
  const wasInSettings = useRef(false);
  // Kullanıcı bu oturumda favorilere dokunduysa sunucudan gelen yanıt onu EZMESİN
  const favTouchedRef = useRef(false);

  // Açılışta sunucuyla eşitle. Sunucuda kayıt varsa o geçerlidir; yoksa ama tarayıcıda
  // varsa yukarı taşınır — böylece favorisi hâlâ duran kullanıcılarda liste bir daha
  // kaybolmaz (silinenler geri gelmez, veri yalnız tarayıcıdaydı).
  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    userPrefService.getAll()
      .then((r) => {
        if (cancelled || favTouchedRef.current) return;
        const remote = r?.data?.prefs?.[FAV_PREF_KEY];
        const local = loadFavorites();
        if (Array.isArray(remote)) {
          const list = remote.filter((x) => typeof x === 'string');
          setFavorites(list);
          saveFavorites(list);
        } else if (local.length) {
          userPrefService.set(FAV_PREF_KEY, local).catch(() => {});
        }
      })
      .catch(() => {});   // tercih okunamazsa yereldekiyle devam — menü çalışmaya devam etmeli
    return () => { cancelled = true };
  }, [userId]);

  const isFav = (p) => favorites.includes(p);
  const toggleFav = (p) => {
    // State güncelleyicinin İÇİNDE yan etki yok (StrictMode iki kez çağırır → çift istek).
    const next = favorites.includes(p) ? favorites.filter((x) => x !== p) : [...favorites, p];
    favTouchedRef.current = true;
    setFavorites(next);
    saveFavorites(next);
    userPrefService.set(FAV_PREF_KEY, next).catch(() => {});
  };

  // Yetki süzgeci navStructure'da — Header sayfa araması da aynı kuralı kullanır.
  const filterItems = (items = []) => filterNavItems(items, { hasPermission, userRole, partnerIsAdmin });
  const visibleGroups = NAV_STRUCTURE.map((g) => ({
    ...g,
    visibleItems: filterItems(g.items || []),
  })).filter((g) => g.visibleItems.some((it) => !it.section));

  // Route değişince aktif grup otomatik seçilsin — en uzun (en özel) eşleşme kazanır
  // (/sales/summary Raporlama'da, /sales/orders Satış'ta).
  const matchLen = (it, path) => {
    if (!it.path) return 0;
    if (it.path === '/dashboard') return path === it.path ? it.path.length : 0;
    return (path === it.path || path.startsWith(it.path + '/') || path.startsWith(it.path)) ? it.path.length : 0;
  };
  const routeGroup = (() => {
    let best = null, bestLen = 0;
    for (const g of visibleGroups) {
      if (!g.label) continue;
      for (const it of g.visibleItems) {
        const l = matchLen(it, location.pathname);
        if (l > bestLen) { best = g; bestLen = l; }
      }
    }
    return best;
  })();
  useEffect(() => {
    if (!routeGroup) { wasInSettings.current = false; return; }
    if (routeGroup.id !== activeGroupId) {
      setActiveGroupId(routeGroup.id);
      saveActiveGroup(routeGroup.id);
    }
    // Ayarlar şeritte yok (dişliden girilir) → ayarlara GİRİNCE panel kendiliğinden açılır
    const nowIn = routeGroup.id === SETTINGS_GROUP_ID;
    if (nowIn && !wasInSettings.current) { setPanelOpen(true); savePanelOpen(true); }
    wasInSettings.current = nowIn;
    // eslint-disable-next-line
  }, [location.pathname]);
  // Ayarlar şeritte yalnız ayarlar sayfasındayken görünür (paneli kapatıp yeniden açabilmek için)
  const inSettings = routeGroup?.id === SETTINGS_GROUP_ID;
  const railGroups = visibleGroups.filter((g) => !g.hiddenInRail || (g.id === SETTINGS_GROUP_ID && inSettings));

  // Favori grubu (sanal): kullanıcının sabitlediği sayfalar, yetki filtreli.
  const favItems = filterItems(favorites.map((p) => PATH_INDEX[p]).filter(Boolean));
  const favGroup = { id: FAV_ID, label: 'Favoriler', icon: Star, visibleItems: favItems };

  const activeGroup = activeGroupId === FAV_ID
    ? favGroup
    : visibleGroups.find((g) => g.id === activeGroupId && g.label);
  const showPanel = !!activeGroup && panelOpen;

  const toggleGroup = (group) => {
    if (!group.label) return; // flat → doğrudan link (button değil)
    // Aynı grup zaten açıksa daralt; değilse o grubu aç
    if (activeGroupId === group.id && panelOpen) {
      setPanelOpen(false); savePanelOpen(false);
    } else {
      setActiveGroupId(group.id); saveActiveGroup(group.id);
      setPanelOpen(true); savePanelOpen(true);
    }
  };

  const closePanel = () => {
    setPanelOpen(false); savePanelOpen(false);
  };

  // Bir sekmeye (sayfaya) girince paneli daralt — ikon rail kalır, ikona tıklayınca tekrar açılır
  const handleItemClick = () => {
    setPanelOpen(false); savePanelOpen(false);
    setMobileSidebarOpen(false);
  };
  // ── İKON RAYI: ikonlar HER ZAMAN sığar ────────────────────────────────────
  // Sabit 40px düğme + 18px ikonla ray, modül sayısı arttıkça (ya da ekran
  // kısaldıkça) taşıp kaydırmalı oluyordu: alttaki modüller görünmüyor,
  // kullanıcı kaydırma çubuğunu fark etmiyordu (2026-09-17 bildirimi).
  //
  // Çözüm sabit bir "daha küçük" değer DEĞİL — bugün sığdıran sayı yarın yeni
  // bir modül eklenince yine taşar. Kullanılabilir yükseklik ölçülür, düğme
  // boyutu ondan TÜRETİLİR ve alt/üst sınıra kırpılır.
  const railNavRef = useRef(null);
  const railH = useElementHeight(railNavRef);
  // ⚠️ TİTREME DÖNGÜSÜ (13.10.2026): ölçüm eskiden kaydırılabilir <nav>'ın kendisinden
  // yapılıyordu ve dolgu hesaba katılmıyordu. Kısa ekranda düğmeler birkaç piksel taşınca
  // kaydırma çubuğu çıkıyor (Windows'ta yer kaplar; dar rayda yatay çubuğu da getirir) →
  // ölçülen yükseklik küçülüyor → düğmeler küçülüyor → çubuk kayboluyor → yükseklik büyüyor
  // → düğmeler büyüyor… Artık ölçüm kaydırma çubuğundan ETKİLENMEYEN dış kaptan yapılır,
  // dolgu ve ayraç tam düşülür, yatay taşma kapalıdır.
  const RAIL_PAD = 16;             // nav py-2
  const railBtnCount = railGroups.length + 1;   // ⭐ + gruplar
  const railFit = (() => {
    const MIN_BTN = 26, MAX_BTN = 40;
    // Ölçüm yoksa (ResizeObserver desteklenmiyor) en büyük boyut: küçültmemek,
    // yanlış küçültmekten iyidir.
    if (!railH || !railBtnCount) return { btn: MAX_BTN, icon: 18, gap: 4 };
    // Öğeler: ⭐ + 1px ayraç + gruplar → aralık sayısı = düğme sayısı
    const fit = (gap) => Math.floor((railH - RAIL_PAD - 1 - gap * railBtnCount) / railBtnCount);
    let gap = 4;
    let raw = fit(gap);
    // Çok sıkışıkta boşluğu da kıs — son çare olarak 2px
    if (raw <= MIN_BTN + 2) { gap = 2; raw = fit(gap); }
    const btn = Math.max(MIN_BTN, Math.min(MAX_BTN, raw));
    // İkon düğmenin ~%45'i (40→18 oranı korunur), 13–18 arasına kırpılır
    const icon = Math.max(13, Math.min(18, Math.round(btn * 0.45)));
    return { btn, icon, gap };
  })();
  const railBtnStyle = { width: railFit.btn, height: railFit.btn };

  const handleLogout = () => {
    useAuthStore.getState().logout();
    window.location.href = '/login';
  };

  return (
    <>
      {mobileSidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-[38] lg:hidden"
          onClick={() => setMobileSidebarOpen(false)}
        />
      )}

      <div
        className={`
          fixed lg:relative top-0 left-0 h-screen lg:h-auto z-40 flex gap-1 lg:gap-2
          transition-transform duration-200 ease-in-out
          ${mobileSidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        `}
      >
        {/* === İKON RAIL === */}
        <aside
          className={`
            w-14 lg:w-16 flex flex-col bg-white min-h-0
            lg:rounded-2xl lg:border lg:border-gray-100 lg:shadow-sm
          `}
        >
          {/* Logo */}
          <div className="px-3 pt-4 pb-3 flex items-center justify-center shrink-0">
            <div
              className="w-9 h-9 rounded-lg bg-primary-500 text-white flex items-center justify-center font-bold"
              title={`${companyName} — VERTEX`}
            >
              V
            </div>
          </div>

          {/* Grup ikonları */}
          <div ref={railNavRef} className="flex-1 min-h-0 overflow-hidden flex flex-col">
          <nav className="h-full py-2 px-1 overflow-y-auto overflow-x-hidden flex flex-col items-center"
               style={{ rowGap: railFit.gap }}>
            {/* Favoriler — En Çok Kullanılanlar (kullanıcı sabitler) */}
            <button
              onClick={() => toggleGroup(favGroup)}
              title="Favoriler"
              style={railBtnStyle}
              className={`relative shrink-0 flex items-center justify-center rounded-lg transition-colors ${
                activeGroupId === FAV_ID
                  ? 'bg-amber-50 text-amber-500'
                  : 'text-gray-500 hover:bg-gray-100 hover:text-amber-500'
              }`}
            >
              <Star size={railFit.icon} fill={activeGroupId === FAV_ID ? 'currentColor' : 'none'} />
            </button>
            <div className="shrink-0 w-6 h-px bg-gray-100" />
            {railGroups.map((group) => {
              const Icon = group.icon;
              const isFlat = !group.label;
              const anyActive = isFlat
                ? group.visibleItems.some((it) => matchLen(it, location.pathname) > 0)
                : routeGroup?.id === group.id;
              const isPanelOpen = activeGroupId === group.id;

              if (isFlat) {
                const only = group.visibleItems[0];
                return (
                  <Link
                    key={group.id}
                    to={only.path}
                    onClick={handleItemClick}
                    title={only.label}
                    style={railBtnStyle}
                    className={`relative shrink-0 flex items-center justify-center rounded-lg transition-colors ${
                      anyActive
                        ? 'bg-primary-500 text-white'
                        : 'text-gray-500 hover:bg-gray-100 hover:text-gray-900'
                    }`}
                  >
                    <Icon size={railFit.icon} />
                    {group.id === 'chat' && chatUnread > 0 && (
                      <span className="absolute top-0.5 right-0.5 min-w-[14px] h-[14px] px-1 rounded-full bg-red-500 text-white text-[9px] font-semibold flex items-center justify-center">
                        {chatUnread > 99 ? '99+' : chatUnread}
                      </span>
                    )}
                  </Link>
                );
              }

              const badge = group.id === 'chat' ? chatUnread : 0;

              return (
                <button
                  key={group.id}
                  onClick={() => toggleGroup(group)}
                  title={group.label}
                  style={railBtnStyle}
                  className={`relative shrink-0 flex items-center justify-center rounded-lg transition-colors ${
                    isPanelOpen
                      ? 'bg-primary-50 text-primary-600'
                      : anyActive
                      ? 'bg-gray-100 text-primary-600'
                      : 'text-gray-500 hover:bg-gray-100 hover:text-gray-900'
                  }`}
                >
                  <Icon size={railFit.icon} />
                  {badge > 0 && (
                    <span className="absolute top-0.5 right-0.5 min-w-[14px] h-[14px] px-1 rounded-full bg-red-500 text-white text-[9px] font-semibold flex items-center justify-center">
                      {badge > 99 ? '99+' : badge}
                    </span>
                  )}
                </button>
              );
            })}
          </nav>
          </div>

          {/* Alt: Çıkış — ⚠️ boyutu SABİT (36px), railFit'e BAĞLANMAZ (20.10.2026).
              Bu alan ölçülen kabın kardeşidir: düğmesi railFit.btn ile büyüyüp küçülseydi
              ölçülen yükseklik düğme boyutuna bağlanır → btn büyür → alt alan büyür → rail
              küçülür → btn küçülür … Bazı ekran yüksekliklerinde iki değer arasında durmadan
              gidip gelir (13.10'daki titremenin ikinci kaynağı, "yine titriyor" bildirimi). */}
          <div className="p-2 border-t border-gray-100 shrink-0">
            <button
              onClick={handleLogout}
              title="Çıkış"
              className="mx-auto w-9 h-9 flex items-center justify-center rounded-lg text-gray-500 hover:bg-red-50 hover:text-red-600 transition-colors"
            >
              <LogOut size={16} />
            </button>
          </div>
        </aside>

        {/* === MENÜ PANELİ === */}
        {showPanel && (
          <aside
            className={`
              w-64 sm:w-60 lg:w-auto lg:min-w-[14rem] lg:max-w-[18rem] flex flex-col bg-white min-h-0
              lg:rounded-2xl lg:border lg:border-gray-100 lg:shadow-sm
            `}
          >
            {/* Başlık */}
            <div className="px-4 pt-4 pb-3 flex items-center justify-between border-b border-gray-50">
              <h2 className="text-base font-semibold text-gray-900 truncate">{activeGroup.label}</h2>
              <button
                onClick={closePanel}
                title="Kapat"
                className="p-1 hover:bg-gray-100 rounded-md text-gray-400 hover:text-gray-600 transition-colors"
              >
                <PanelLeftClose size={16} />
              </button>
            </div>

            {/* Nav items — tree indent */}
            <nav className="flex-1 py-3 px-3 overflow-y-auto overflow-x-hidden relative">
              {/* dikey çizgi */}
              <div className="absolute top-3 bottom-3 left-4 w-px bg-gray-100" />
              <div className="space-y-0.5 relative">
                {activeGroup.id === FAV_ID && favItems.length === 0 && (
                  <div className="px-3 py-8 text-center">
                    <Star size={22} className="mx-auto text-gray-300 mb-2" />
                    <p className="text-xs text-gray-500 leading-relaxed">
                      Henüz favori yok.<br />
                      Herhangi bir menü panelinde bir sayfanın üzerine gelip
                      <span className="inline-flex items-center mx-1 text-amber-400"><Star size={12} /></span>
                      simgesine tıklayarak buraya ekleyebilirsin.
                    </p>
                  </div>
                )}
                {(() => {
                  // En uzun (en spesifik) prefix match olan tek path active
                  const path = location.pathname;
                  const candidates = activeGroup.visibleItems.filter((it) => it.path && (
                    path === it.path || path.startsWith(it.path + '/')
                  ));
                  candidates.sort((a, b) => (b.path?.length || 0) - (a.path?.length || 0));
                  const activePath = candidates[0]?.path;
                  return activeGroup.visibleItems.map((item, idx) => {
                    if (item.section) {
                      return (
                        <div
                          key={`section-${idx}`}
                          className={`px-3 ${idx > 0 ? 'pt-3' : 'pt-0.5'} pb-1 text-[10px] font-semibold uppercase tracking-wider text-gray-400`}
                        >
                          {item.section}
                        </div>
                      );
                    }
                    return (
                      <PanelItem
                        key={item.path}
                        item={item}
                        isActive={item.path === activePath}
                        onClick={handleItemClick}
                        isFav={isFav(item.path)}
                        onToggleFav={toggleFav}
                      />
                    );
                  });
                })()}
              </div>
            </nav>

            {/* Panel alt: firma adı */}
            <div className="px-4 py-3 border-t border-gray-50">
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-md bg-primary-50 text-primary-600 flex items-center justify-center text-xs font-bold">
                  {companyName[0]}
                </div>
                <div className="flex flex-col leading-tight">
                  <span className="text-xs font-semibold text-gray-900">{companyName}</span>
                  <span className="text-[10px] text-gray-400 tracking-wide">VERTEX</span>
                </div>
              </div>
            </div>
          </aside>
        )}
      </div>
    </>
  );
}
